import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { trackEvent } from "@/lib/trackEvent";

interface NavArticle {
  slug: string;
  headline: string;
}

interface Props {
  previous?: NavArticle | null;
  next?: NavArticle | null;
  articleId?: string;
  slug?: string;
}

export default function ArticleNav({ previous, next, articleId, slug }: Props) {
  if (!previous && !next) return null;

  const track = (direction: string, toSlug: string) =>
    trackEvent("related_article_clicked", {
      placement: `nav_${direction}`,
      from_article_id: articleId,
      from_slug: slug,
      to_slug: toSlug,
    });

  return (
    <nav className="grid gap-4 sm:grid-cols-2 py-10 border-t border-border/50">
      {previous ? (
        <Link
          to={`/articles/${previous.slug}`}
          onClick={() => track("previous", previous.slug)}
          className="group rounded-xl border border-border/60 p-5 hover:border-primary/50 transition-colors"
        >
          <span className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground mb-2">
            <ArrowLeft className="w-3 h-3" /> Previous article
          </span>
          <span className="block font-semibold text-base leading-snug group-hover:text-primary transition-colors line-clamp-2">
            {previous.headline}
          </span>
        </Link>
      ) : (
        <span />
      )}
      {next && (
        <Link
          to={`/articles/${next.slug}`}
          onClick={() => track("next", next.slug)}
          className="group rounded-xl border border-border/60 p-5 hover:border-primary/50 transition-colors sm:text-right"
        >
          <span className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground mb-2 sm:justify-end">
            Next article <ArrowRight className="w-3 h-3" />
          </span>
          <span className="block font-semibold text-base leading-snug group-hover:text-primary transition-colors line-clamp-2">
            {next.headline}
          </span>
        </Link>
      )}
    </nav>
  );
}
